/* Theme-owned integration contract. No vendor loaders, pixels or network calls. */
(() => {
  'use strict';
  if (window.TorqueIntegrations) return;
  let sequence = 0;
  let cartRevision = 0;
  let cartPending = 0;
  let cartTail = Promise.resolve();
  const cartQueue = {
    get revision() { return cartRevision; },
    get pending() { return cartPending; },
    run(task) {
      cartPending++;
      document.dispatchEvent(new CustomEvent('torque:cart:queue', { detail: { pending: cartPending } }));
      const result = cartTail.then(task);
      // A rejected request must not block the shopper's next request.
      cartTail = result.then(() => {}, () => {});
      return result.finally(() => {
        cartPending--;
        document.dispatchEvent(new CustomEvent('torque:cart:queue', { detail: { pending: cartPending } }));
      });
    },
    invalidate(cart, source = 'theme') {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { cart, source, revision: ++cartRevision },
      }));
    },
  };
  const number = value => value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
  const item = value => ({
    productId: value.product_id == null ? null : String(value.product_id),
    variantId: value.variant_id == null && value.id == null ? null : String(value.variant_id ?? value.id),
    key: value.key || null, quantity: number(value.quantity),
    unitPrice: number(value.final_price ?? value.price), linePrice: number(value.final_line_price),
  });
  function publish(type, { source, cart, items = [], operation, reason, requestedQuantity } = {}) {
    // Do not expose cart tokens, notes, customer details or custom properties.
    const detail = {
      version: 1, sequence: ++sequence, type, source: source || 'theme',
      operation: operation || null, reason: reason || null,
      requestedQuantity: number(requestedQuantity),
      currency: typeof cart?.currency === 'string' ? cart.currency : window.Shopify?.currency?.active || null,
      moneyUnit: 'shopify-subunits', items: items.map(item),
      cart: cart ? { itemCount: number(cart.item_count), totalPrice: number(cart.total_price), items: (cart.items || []).map(item) } : null,
    };
    document.dispatchEvent(new CustomEvent('torque:commerce', { detail }));
    if (type === 'cart:add' || type === 'cart:change') cartQueue.invalidate(cart, detail.source);
    return detail;
  }
  function rendered(root, surface) {
    document.dispatchEvent(new CustomEvent('torque:cart:rendered', {
      detail: { root, surface, slots: [...root.querySelectorAll('[data-torque-cart-slot]')] },
    }));
  }
  function replaceContent(current, next) {
    // Keep the actual widget node: listeners, input state and references survive.
    for (const placeholder of next.querySelectorAll('[data-torque-cart-slot]')) {
      const previous = [...current.querySelectorAll('[data-torque-cart-slot]')].find(node =>
        node.dataset.torqueCartSlot === placeholder.dataset.torqueCartSlot &&
        node.dataset.cartSurface === placeholder.dataset.cartSurface);
      if (previous) placeholder.replaceWith(previous);
    }
    current.replaceChildren(...next.childNodes);
  }
  window.TorqueIntegrations = { version: 1, publish, rendered, replaceContent, cart: cartQueue };
  document.addEventListener('shopify:section:unload', event => {
    document.dispatchEvent(new CustomEvent('torque:cart:unmount', { detail: { root: event.target } }));
  });
})();

/* Close automatic SAVE $15 popups through the native dismiss control.
 * A deliberate teaser click temporarily permits this campaign only.
 * CSS prevents a flash/overlay; request native dismissal and recover only this
 * campaign's scroll lock if the vendor's mobile animation cleanup stalls.
 * Keep Klaviyo loaded for tracking and all other signup forms.
 */
(() => {
  'use strict';
  if (window.torqueRetiredPopupDismissal) return;
  window.torqueRetiredPopupDismissal = true;
  const selector = '[data-testid="klaviyo-form-Ugufyb"]';
  const dismissed = new WeakSet();
  const watched = new WeakSet();
  const pending = new Set();
  // Give the vendor time to mount and run its 350ms entrance animation before
  // requesting dismissal. Lock recovery also covers delayed vendor effects.
  const dismissDelay = 500;
  let timer = 0;
  const manuallyOpen = () => document.documentElement.dataset.torqueKlaviyoOpen === 'Ugufyb';
  function releaseRetiredScrollLock() {
    if (manuallyOpen()) return;
    const lockClass = 'klaviyo-prevent-body-scrolling';
    if (!document.body.classList.contains(lockClass)) return;
    const modals = [...document.querySelectorAll('[data-kl-scroll-locking-modal="true"]')];
    // Only this suppressed campaign may relinquish Klaviyo's lock. Preserve
    // every other popup, body style, and the theme's cart/menu/dialog locks.
    if (!modals.some(dialog => dialog.querySelector(selector))) return;
    if (modals.some(dialog => !dialog.querySelector(selector))) return;
    document.body.classList.remove(lockClass);
  }
  function queue(form) {
    pending.add(form);
    if (!timer) timer = window.setTimeout(flush, dismissDelay);
  }
  function watch(dialog, form) {
    if (watched.has(dialog)) return;
    watched.add(dialog);
    // Klaviyo can reuse a mounted dialog. Watch only that dialog's lifecycle,
    // never every style mutation on the storefront.
    new MutationObserver(() => {
      if (manuallyOpen() && (dialog.style.display === 'none' || dialog.style.animationName === 'klaviyo-fadeout')) {
        document.dispatchEvent(new CustomEvent('torque:signup:vendor-closed'));
      }
      if (dialog.style.display === 'none') dismissed.delete(dialog);
      else queue(form);
      releaseRetiredScrollLock();
    }).observe(dialog, { attributes: true, attributeFilter: ['style', 'data-kl-scroll-locking-modal'] });
  }
  function flush() {
    timer = 0;
    const forms = [...pending];
    pending.clear();
    for (const form of forms) {
      if (!form.isConnected) continue;
      const dialog = form.closest('[role="dialog"]');
      if (!dialog) continue;
      watch(dialog, form);
      if (manuallyOpen()) {
        // Reused vendor dialogs do not always emit another klaviyoForms open.
        if (dialog.style.display !== 'none' && dialog.style.animationName !== 'klaviyo-fadeout') {
          document.dispatchEvent(new CustomEvent('torque:signup:opened'));
        }
        continue;
      }
      releaseRetiredScrollLock();
      if (dialog.style.display === 'none') {
        dismissed.delete(dialog);
        continue;
      }
      const close = dialog?.querySelector('button.klaviyo-close-form');
      if (!close || dismissed.has(dialog)) continue;
      // Mark BEFORE clicking: vendor animations can mutate the dialog immediately.
      dismissed.add(dialog);
      close.click();
      releaseRetiredScrollLock();
    }
  }
  function discover(node) {
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    if (node.matches(selector)) pending.add(node);
    node.querySelectorAll(selector).forEach(form => pending.add(form));
    // The close control may mount after its form, as a sibling inside the dialog.
    const dialog = node.closest('[role="dialog"]');
    dialog?.querySelectorAll(selector).forEach(form => pending.add(form));
    // Yield to rendering and the vendor's scroll-unlock timer between batches.
    if (pending.size && !timer) timer = window.setTimeout(flush, dismissDelay);
  }
  const observer = new MutationObserver(records => {
    for (const record of records) record.addedNodes.forEach(discover);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  // A vendor effect may apply the class after the native close was requested.
  // Observe only the body's class, not style churn throughout the page.
  new MutationObserver(releaseRetiredScrollLock).observe(document.body, {
    attributes: true, attributeFilter: ['class'],
  });
  discover(document.body);
  releaseRetiredScrollLock();
  document.addEventListener('torque:signup:closed', () => {
    discover(document.body);
    releaseRetiredScrollLock();
  });
})();
